import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, Ban, CheckCircle2, ChevronDown, ChevronUp, Circle, MinusCircle } from 'lucide-react';
import type { ReadinessChecklistItem, ReadinessItemStatus } from '../paidBetaReadiness';
import { formatSetupChecklistProgress, isOperationalReadinessKey } from '../leagueReadinessMapper';

interface PaidBetaReadinessChecklistProps {
  items: ReadinessChecklistItem[];
  completedCount: number;
  totalCount?: number;
  loading?: boolean;
  setupComplete?: boolean;
  shouldShowChecklist?: boolean;
  summaryLabel?: string;
}

function StatusIcon({ status }: { status: ReadinessItemStatus }) {
  switch (status) {
    case 'complete':
      return <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" aria-hidden />;
    case 'blocked':
      return <Ban className="h-5 w-5 text-red-400 shrink-0 mt-0.5" aria-hidden />;
    case 'not_applicable':
      return <MinusCircle className="h-5 w-5 text-secondary-500 shrink-0 mt-0.5" aria-hidden />;
    default:
      return <Circle className="h-5 w-5 text-secondary-500 shrink-0 mt-0.5" aria-hidden />;
  }
}

const statusLabel: Record<ReadinessItemStatus, string> = {
  complete: 'Ready',
  warning: 'Needs attention',
  blocked: 'Needs setup',
  not_applicable: 'Not applicable',
};

const PaidBetaReadinessChecklist: React.FC<PaidBetaReadinessChecklistProps> = ({
  items,
  completedCount,
  totalCount,
  loading = false,
  setupComplete = false,
  shouldShowChecklist = true,
  summaryLabel,
}) => {
  const [expanded, setExpanded] = useState(!setupComplete && shouldShowChecklist);

  useEffect(() => {
    if (setupComplete || !shouldShowChecklist) {
      setExpanded(false);
    }
  }, [setupComplete, shouldShowChecklist]);

  if (!loading && shouldShowChecklist === false) {
    return null;
  }

  const setupItems = items.filter((item) => !isOperationalReadinessKey(item.id));
  const visibleItems = setupItems.filter(
    (item) => item.status === 'blocked' || item.status === 'warning' || item.isActionable
  );
  const displayItems = visibleItems.length > 0 ? visibleItems : setupItems;
  const resolvedTotalCount = totalCount ?? setupItems.length;
  const progressLabel = formatSetupChecklistProgress(completedCount, resolvedTotalCount);

  return (
    <section className="rounded-xl border border-secondary-700 bg-secondary-900/60" data-testid="paid-beta-readiness-checklist">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-secondary-800 px-4 py-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-white">League setup checklist</h2>
            {summaryLabel ? (
              <span className="rounded-full border border-secondary-700 bg-secondary-900/60 px-2 py-0.5 text-xs text-secondary-300">
                {summaryLabel}
              </span>
            ) : null}
          </div>
          <p className="text-xs text-secondary-500 mt-0.5">
            {loading ? 'Loading setup checklist…' : progressLabel}
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-2 text-sm"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          data-testid="paid-beta-readiness-toggle"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4" aria-hidden />
              Hide checklist
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" aria-hidden />
              Show checklist
            </>
          )}
        </button>
      </div>

      {expanded ? (
        <ol className="space-y-4 p-4">
          {displayItems.map((item) => (
            <li
              key={item.id}
              className="flex items-start gap-3"
              data-testid={`paid-beta-readiness-item-${item.id}`}
            >
              <StatusIcon status={item.status} />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-white">{item.label}</span>
                  <span className="text-xs rounded-full border border-secondary-700 px-2 py-0.5 text-secondary-400">
                    {statusLabel[item.status]}
                  </span>
                </div>
                <p className="text-xs text-secondary-400">{item.explanation}</p>
                <Link
                  to={item.actionHref}
                  className="inline-flex text-xs font-medium text-primary-300 hover:text-primary-200"
                >
                  {item.actionLabel}
                </Link>
              </div>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
};

export default PaidBetaReadinessChecklist;
